import React, { useEffect, useState, useCallback } from "react";
import { Box, Paper, Typography, Button, Chip, Stack, Alert, CircularProgress, TextField } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { getLicenseChanges, approveLicenseChange, rejectLicenseChange } from "../../services/usageStatsService";

// Fronta žiadostí o zmenu licencie pre superadmina (schváliť / zamietnuť).
const LicenseRequestsQueue = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [rejectNote, setRejectNote] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    getLicenseChanges("pending")
      .then((d) => setRows(d.changes || []))
      .catch((e) => setError(e.message || "Chyba"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const doApprove = async (id) => {
    setBusy(id); setError("");
    try { await approveLicenseChange(id); load(); }
    catch (e) { setError(e.message); } finally { setBusy(null); }
  };
  const doReject = async (id) => {
    setBusy(id); setError("");
    try { await rejectLicenseChange(id, rejectNote); setRejectId(null); setRejectNote(""); load(); }
    catch (e) { setError(e.message); } finally { setBusy(null); }
  };

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, border: "1px solid", borderColor: "divider", mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Žiadosti o zmenu licencie</Typography>
        <Chip size="small" color={rows.length ? "warning" : "default"} label={`${rows.length} čaká`} />
      </Box>
      {loading && <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>}
      {error && <Alert severity="error" sx={{ mb: 1, borderRadius: 2 }}>{error}</Alert>}
      {!loading && rows.length === 0 && (
        <Typography variant="body2" color="text.secondary">Žiadne čakajúce žiadosti.</Typography>
      )}
      <Stack spacing={1.5}>
        {rows.map((c) => (
          <Box key={c.id} sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {c.tenant_name} — {c.from_program || "—"} → {c.to_program || "—"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Žiadateľ: {c.requested_by || "—"} · {c.to_price || ""}
                  {c.custom_snapshot && c.custom_snapshot.docs_limit != null ? ` · limit ${c.custom_snapshot.docs_limit}, kontá ${c.custom_snapshot.user_accounts ?? "—"}` : ""}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="contained" color="success" disableElevation startIcon={<CheckCircleOutlineIcon />}
                  disabled={busy === c.id} onClick={() => doApprove(c.id)} sx={{ borderRadius: 2 }}>
                  Schváliť
                </Button>
                <Button size="small" variant="outlined" color="error" startIcon={<HighlightOffIcon />}
                  disabled={busy === c.id} onClick={() => setRejectId(rejectId === c.id ? null : c.id)} sx={{ borderRadius: 2 }}>
                  Zamietnuť
                </Button>
              </Stack>
            </Box>
            {rejectId === c.id && (
              <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                <TextField size="small" fullWidth placeholder="Dôvod zamietnutia (voliteľné)" value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} />
                <Button size="small" color="error" variant="contained" disableElevation disabled={busy === c.id} onClick={() => doReject(c.id)}>Potvrdiť</Button>
              </Box>
            )}
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

export default LicenseRequestsQueue;
